<template>
  <DynamicControls
    ref="list"
    :configFields="configFields"
    :configValues="configValues"
    :handleValueChange="_dataKeyChanged"
  />
</template>
<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import {
  applicationModelFields,
} from 'client/util/Constants/ApplicationConstants';
import { cloneDeep } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import ApplicationDynamicControlsMixin from 'client/applications/mixins/ApplicationDynamicControlsMixin';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';

const applicationModel = StoreModelMap[ItemTypeConsts.sso];

const {
  JITATTRIBUTE_TYPE_CONSTANT,
  JITATTRIBUTE_TYPE_DB,
  CONFIG,
  CONSTANT_ATTRIBUTES,
  DATABASE_ATTRIBUTES,
  GROUPS_ATTRIBUTES_NAME,
  INCLUDE_GROUPS,
} = applicationModelFields;

export default {
  name: 'ApplicationDetailsSecondaryDynamicControls',

  inject: ['$validator'],

  mixins: [
    ApplicationDynamicControlsMixin,
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  data() {
    return {
      configFields: [],
      configValues: [],
    };
  },

  computed: {
    ...mapGetters(applicationModel, [
      'isItemBeingCreated',
      'isItemBeingUpdated',
      'templateConstantAttributesValue',
    ]),

    ...mapState(applicationModel, [
      'template',
    ]),
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  created() {
    this.handleCreated();
  },

  methods: {
    _dataKeyChanged(key, value) {
      const { keyInformation } = this.dataKeyChanged(key, value);
      this.includeGroupsValueChange({ key, keyInformation });
    },

    addJITAttributesToggles(attributes, type = undefined) {
      const item = cloneDeep(this.item);
      const jit = cloneDeep(this.template.jit);
      const config = cloneDeep(item.config);
      // We may need to add a getter for jit here
      const jitTemplateConfig = jit;

      if (!jitTemplateConfig) return attributes;

      const jitTemplateDictionary = this.getTemplateJitDictionary();

      const {
        customAttributes,
        jitCustomDictionary,
        rootAttributes,
      } = this.getJitCustomDictionary(attributes, jitTemplateDictionary);

      let jitTemplateConfigAttributes = this.getJitConfigAttributes(
        config,
        jitCustomDictionary,
        jitTemplateDictionary,
        jitTemplateConfig,
        type,
      );

      jitTemplateConfigAttributes = this.setDefaultConstantAttributesValue(
        jitTemplateConfigAttributes,
        type,
      );

      const sortedAttributes = rootAttributes
        .concat(jitTemplateConfigAttributes)
        .concat(customAttributes);

      this.setItemField({ key: CONFIG, value: config });
      return sortedAttributes;
    },
    // set the default and custom attributes values
    // (databaseAttributes, constantAttributes, groupsAttributeName, includeGroups)
    // the result given would be the attributes to display them as dynamic controls
    getAttributesGroups(item) {
      const config = cloneDeep(item.config);
      let baseDatabaseAttributes = item.databaseAttributes;

      const {
        databaseAttributes,
        constantAttributes,
        includeGroups,
        groupsAttributeName,
      } = config;

      databaseAttributes.value = this.addUniqueIdToConstantAttributesChildren(databaseAttributes);
      constantAttributes.value = this.addUniqueIdToConstantAttributesChildren(constantAttributes)
        // constant attributes are never strict required.
        .map(attr => ({ ...attr, required: false }));
      baseDatabaseAttributes = baseDatabaseAttributes
        .map(attr => ({ ...attr, tempId: this.getUniqueID() }));

      groupsAttributeName.visible = includeGroups.value;

      config.databaseAttributes = databaseAttributes;
      config.constantAttributes = constantAttributes;
      config.groupsAttributeName = groupsAttributeName;
      config.includeGroups = includeGroups;

      // update the model to including the unique ids
      this.setItemField({ key: CONFIG, value: cloneDeep(config) });
      this.setPreviousItemField({ key: CONFIG, value: cloneDeep(config) });
      this.setItemField({ key: DATABASE_ATTRIBUTES, value: cloneDeep(baseDatabaseAttributes) });
      this.setPreviousItemField({
        key: DATABASE_ATTRIBUTES,
        value: cloneDeep(baseDatabaseAttributes),
      });

      // mapping root attributes, they should be readonly
      if (baseDatabaseAttributes) {
        databaseAttributes.value = baseDatabaseAttributes.map(x => ({
          ...x,
          isRootAttribute: true,
          visible: true,
          readOnly: false,
          nameReadOnly: true,
        })).concat(databaseAttributes.value);
      }

      return {
        databaseAttributes,
        constantAttributes,
        groupsAttributeName,
        includeGroups,
      };
    },

    getDynamicControlsProperties() {
      const model = cloneDeep(this.item);

      const {
        databaseAttributes,
        constantAttributes,
        groupsAttributeName,
        includeGroups,
      } = this.getAttributesGroups(model);

      databaseAttributes.value = this.addJITAttributesToggles(
        databaseAttributes.value,
        JITATTRIBUTE_TYPE_DB,
      );
      constantAttributes.value = this.addJITAttributesToggles(
        constantAttributes.value,
        JITATTRIBUTE_TYPE_CONSTANT,
      );

      const groups = [
        [DATABASE_ATTRIBUTES, databaseAttributes],
        [CONSTANT_ATTRIBUTES, constantAttributes],
        [INCLUDE_GROUPS, includeGroups],
        [GROUPS_ATTRIBUTES_NAME, groupsAttributeName],
      ];

      return groups.reduce((previous, current) => this.reduceLegacyDynamicControlConfig({
        previous,
        current,
      }), {
        configFields: [],
        configValues: [],
      });
    },

    getJitConfigAttributes(
      config,
      jitCustomDictionary,
      jitTemplateDictionary,
      jitTemplateConfig,
      type,
    ) {
      return jitTemplateConfig.attributes.reduce((previous, current) => {
        const array = previous;
        const { name } = current;
        const jitAttr = jitTemplateDictionary[name];
        let customAttr = jitCustomDictionary[name];

        if (jitAttr.type !== type) return array;

        if (!customAttr) {
          customAttr = {
            isCustomAttribute: true,
            label: name,
            mutable: true,
            name,
            nameReadOnly: true,
            tempId: this.getUniqueID(),
            value: jitAttr.value || '',
            valueReadOnly: this.isItemBeingUpdated,
            toggleInput: {
              value: this.isItemBeingCreated,
            },
          };

          if (this.isItemBeingCreated) {
            if (jitAttr.type === JITATTRIBUTE_TYPE_CONSTANT) {
              config.constantAttributes.value.push(customAttr);
            } else config.databaseAttributes.value.push(customAttr);
          }
        }

        if (!customAttr.isRootAttribute) array.push(customAttr);

        return array;
      }, []);
    },

    getJitCustomDictionary(attributes, jitDictionary) {
      const rootAttributes = [];
      const customAttributes = [];

      const jitCustomDictionary = attributes.reduce((previous, current) => {
        const dictionary = previous;
        const attr = current;

        if (attr.isRootAttribute) rootAttributes.push(attr);

        if (!dictionary[attr.name] && jitDictionary[attr.name]) {
          dictionary[attr.name] = attr;
          if (!attr.isRootAttribute) {
            attr.nameReadOnly = true;
            attr.valueReadOnly = false;
            attr.value = dictionary[attr.name].value || jitDictionary[attr.name].value;
            attr.toggleInput = {
              value: true,
            };
          }
        } else if (!attr.isRootAttribute) customAttributes.push(attr);

        return dictionary;
      }, {});

      return {
        customAttributes,
        jitCustomDictionary,
        rootAttributes,
      };
    },

    handleCreated() {
      const {
        configFields,
        configValues,
      } = this.getDynamicControlsProperties();
      this.configFields = configFields;
      this.configValues = configValues;
    },

    includeGroupsValueChange({ key, keyInformation }) {
      if (key === INCLUDE_GROUPS) {
        const config = cloneDeep(this.item.config);
        const attributeGroupNameConfig = config[GROUPS_ATTRIBUTES_NAME];
        const attributesNameConfigField = this.getConfigFieldKey(GROUPS_ATTRIBUTES_NAME);

        attributesNameConfigField.hide = !keyInformation.value;

        if (!attributesNameConfigField.hide && !attributeGroupNameConfig.value) {
          const template = this.template || {};
          const groupsAttributesNameTemplate = template[GROUPS_ATTRIBUTES_NAME] || {};
          attributesNameConfigField.value = groupsAttributesNameTemplate.value || '';
          attributeGroupNameConfig.value = attributesNameConfigField.value;
        }

        this.setItemField({ key: CONFIG, value: config });
      }
    },

    setDefaultConstantAttributesValue(constantAttributes, attributeType) {
      if (attributeType !== JITATTRIBUTE_TYPE_CONSTANT) return constantAttributes;

      const constantAttributesDictionary = this.templateConstantAttributesValue;

      return constantAttributes.map((constantAttribute) => {
        let attribute = constantAttribute;
        if (!attribute.value) {
          const value = (constantAttributesDictionary[attribute.name]
            && constantAttributesDictionary[attribute.name].value) || '';
          attribute = { ...attribute, value };
        }
        return attribute;
      });
    },
  },
};
</script>
